import React from 'react';
import PlaybookStatusBadge from './PlaybookStatusBadge';
import type { PlaybookEntity, PlaybookStatusFull } from '../types/playbook.types';

interface PlaybookStructureTabProps {
  playbook: PlaybookEntity;
}

const PlaybookStructureTab: React.FC<PlaybookStructureTabProps> = ({ playbook }) => {
  const statuses = [...(playbook.statuses || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const transitions = playbook.transitions || [];
  const statusMap = new Map<string, PlaybookStatusFull>(statuses.map((s) => [s.id, s]));

  return (
    <div className="space-y-4">
      {/* Status list */}
      <div>
        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          Statuses ({statuses.length})
        </h4>
        <div className="space-y-1">
          {statuses.map((s, idx) => (
            <div key={s.id} className="flex items-center gap-2 py-1.5 px-3 rounded-md bg-gray-50 dark:bg-gray-800/50">
              <span className="text-[10px] text-gray-400 w-5 text-right">{idx + 1}</span>
              <PlaybookStatusBadge category={s.category} name={s.name} />
              <span className="text-xs text-gray-500 dark:text-gray-400">{s.slug}</span>
              {s.is_initial && (
                <span className="ml-auto text-[10px] font-medium text-lex-accent">INITIAL</span>
              )}
              {s.is_terminal && (
                <span className="ml-auto text-[10px] font-medium text-emerald-600 dark:text-emerald-400">TERMINAL</span>
              )}
              {s.sla_hours != null && (
                <span className="text-[10px] text-gray-400 dark:text-gray-500">SLA: {s.sla_hours}h</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Transitions */}
      <div>
        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          Transitions ({transitions.length})
        </h4>
        <div className="space-y-1">
          {transitions.map((t) => {
            const fromStatus = t.from_status_id ? statusMap.get(t.from_status_id) : null;
            const toStatus = t.to_status_id ? statusMap.get(t.to_status_id) : null;
            return (
              <div key={t.id} className="flex items-center gap-2 py-1.5 px-3 rounded-md bg-gray-50 dark:bg-gray-800/50">
                <span className="text-xs font-medium text-gray-900 dark:text-white">{t.name}</span>
                <span className="text-[10px] text-gray-400">
                  {t.is_global ? '(global)' : fromStatus?.name || '?'} → {toStatus?.name || '?'}
                </span>
                <span className={`ml-auto inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-medium ${
                  t.trigger_mode === 'manual' ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' :
                  t.trigger_mode === 'event' ? 'bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300' :
                  t.trigger_mode === 'deadline' ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300' :
                  'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-300'
                }`}>
                  {t.trigger_mode}
                </span>
                {t.is_collaborative && (
                  <span className="text-[9px] font-medium text-lex-accent">COLLABORATIVE</span>
                )}
                {(t.guards?.length || 0) > 0 && (
                  <span className="text-[9px] text-gray-400">{t.guards?.length}G</span>
                )}
                {(t.validators?.length || 0) > 0 && (
                  <span className="text-[9px] text-gray-400">{t.validators?.length}V</span>
                )}
                {(t.actions?.length || 0) > 0 && (
                  <span className="text-[9px] text-gray-400">{t.actions?.length}A</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PlaybookStructureTab;
